/**
 * Shared game classification + routing.
 *
 * These lists were previously duplicated across Home, BurgerMenu, Create and
 * Room, which let them drift out of sync. Keep them here only.
 */

/** Games that live in one persistent global room rather than per-host rooms. */
export const GLOBAL_GAME_ROUTES: Record<string, string> = {
  'bubble-rush': 'BUBBLE',
  'moka-crash': 'ESPRES',
  'cards-racing': 'CARDSR',
  'horse-racing': 'HORSER',
  'car-racing': 'CARRAC',
  'aether-dynasty': 'AETHER',
  'sky-crash': 'SKYCRS',
  'kana-mutti': 'KANAMT',
  'kamba-adeema': 'KAMBAA',
  'pixel-poker': 'PIXPKR',
  'coin-duel': 'COINDL',
  'collapse-factor': 'CLPSFC',
  'whack-a-mole': 'WHACKM',
};

export const GLOBAL_GAMES = new Set(Object.keys(GLOBAL_GAME_ROUTES));

/**
 * Games that mount their own full-screen standalone SPA (splash → game),
 * so the playground renders no chrome around them and the room create/join
 * flow lives inside the game itself.
 */
export const SELF_CONTAINED_GAMES = new Set([
  'horse-racing',
  'car-racing',
  'aether-dynasty',
  'sky-crash',
  'kana-mutti',
  'kamba-adeema',
  'pixel-poker',
  'coin-duel',
  'collapse-factor',
  'whack-a-mole',
]);

export function isGlobalGame(gameId: string): boolean {
  return GLOBAL_GAMES.has(gameId);
}

/** The fixed room codes above, as a set, for `isGlobalRoomCode`. */
const GLOBAL_ROOM_CODES = new Set(Object.values(GLOBAL_GAME_ROUTES));

/**
 * `true` for one of the fixed rooms the game-server bootstraps at start-up.
 *
 * These codes are hand-picked, not generated, so several of them (HORSER,
 * PIXPKR, COINDL) contain the `O`/`I` that `isValidRoomCode` excludes to keep
 * *typed* codes unambiguous. The rooms provably exist, so callers should treat
 * them as joinable alongside anything that passes the syntactic check.
 */
export function isGlobalRoomCode(code: string): boolean {
  return GLOBAL_ROOM_CODES.has(code);
}

export function isSelfContainedGame(gameId: string): boolean {
  return SELF_CONTAINED_GAMES.has(gameId);
}

/** Where the "Play" affordance for a game should navigate. */
export function gameRoute(gameId: string): string {
  const code = GLOBAL_GAME_ROUTES[gameId];
  return code ? `/join?code=${code}` : `/create?gameId=${gameId}`;
}

/**
 * Thumbnail candidates, tried in order until one loads.
 *
 * The asset set is a mix of .png and .jpg with only some games having a
 * dedicated `-hero` crop, so callers fall back rather than assume.
 */
export function cardThumbCandidates(gameId: string): string[] {
  return [`/thumbnails/${gameId}.png`, `/thumbnails/${gameId}.jpg`];
}

export function heroPosterCandidates(gameId: string): string[] {
  return [
    `/thumbnails/${gameId}-hero.jpg`,
    `/thumbnails/${gameId}-hero.png`,
    `/thumbnails/${gameId}.png`,
    `/thumbnails/${gameId}.jpg`,
  ];
}
